import React from 'react';
import { useAddEditContactModalHook } from './useAddEditContactModalHook';
import { Autocomplete, Modal, TextField, Typography } from '@orijinworks/frontend-commons';
import { Box, withStyles } from '@material-ui/core';
import styles from './add-edit-contact-modal.style';
import ImageDropArea, { ManageContactProvider } from './image-drop-area';
import { FormHelperText, Skeleton } from '@mui/material';

const AddEditContactModal = (props) => {
    const {
        isLoading,
        isEdit,
        modalRef,
        formik,
        residents,
        languages,
        relationshipOptions,
        isSubmitting,
        snackBarProps,
        handleClose,
        handlePhoneChange,
        selectedFile,
        previewUrl,
        isDragging,
        error,
        onDrop,
        onDragEnter,
        onDragLeave,
        onFileInputChange,
        clearSelectedFile,
        relationshipChangeHandler,
        languageChangeHandler,
        residentChangeHandler
    } = useAddEditContactModalHook(props);

    const { classes } = props;

    return (
        <Box className={classes.mainWrapper}>
            <Modal
                ref={modalRef}
                isSnackBarShown={snackBarProps.isShown}
                snackBarProps={snackBarProps}
                cancelBtnText="Cancel"
                actionBtnText={isEdit ? "Update Contact" : "Add Contact"}
                heading={isEdit ? "Edit Video Calls Contact" : "Add a Video Calls Contact"}
                labelledBy="Video Call Modal"
                describedBy="A modal to create or edit video call contact"
                open={true}
                onClose={handleClose}
                onContinue={formik.handleSubmit}
                continueButtonProps={{
                    disabled: isSubmitting || isLoading
                }}
            >
                <form onSubmit={(e) => {
                    e.preventDefault();
                    formik.handleSubmit(e);
                }}>
                    {!isEdit && (
                        <Box marginBottom="30px">
                            <Typography variant='body1'
                                className={classes.mainHeading}>
                                You must <b>first</b> select the resident you want to add a contact for.
                            </Typography>
                            {isLoading && (
                                <Skeleton animation="wave" className={classes.fieldLoader} />
                            )}
                            {!isLoading && (
                                <Autocomplete
                                    label="Search Resident*"
                                    id="resident"
                                    name="resident"
                                    className={classes.autoCompleteField}
                                    fullWidth
                                    value={formik.values.resident}
                                    onChange={residentChangeHandler}
                                    onBlur={formik.handleBlur}
                                    options={residents}
                                    disabled={isEdit}
                                    testId="resident-input"
                                    error={formik.touched.resident && Boolean(formik.errors.resident)}
                                    helperText={formik.touched.resident && formik.errors.resident}
                                />
                            )}
                            {formik.touched.resident && Boolean(formik.errors.resident) && (
                                <FormHelperText className={classes.dangerTxt}>
                                    {formik.touched.resident && formik.errors.resident}
                                </FormHelperText>
                            )}
                        </Box>
                    )}

                    <Box>
                        <Typography variant="overlineMedium" className={classes.contactInformationHeading}>
                            CONTACT INFORMATION
                        </Typography>
                        <Box>
                            {isLoading && (
                                new Array(6).fill(6).map(item => (
                                    <Skeleton
                                        key={item}
                                        animation="wave" className={classes.fieldLoader} />
                                ))
                            )}
                            {!isLoading && (
                                <>
                                    <TextField
                                        label="First Name*"
                                        placeholder='First Name'
                                        className={classes.textInput}
                                        id="firstName"
                                        name="firstName"
                                        value={formik.values.firstName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                                        helperText={formik.touched.firstName && formik.errors.firstName}
                                    />
                                    <TextField
                                        label="Last Name*"
                                        className={classes.textInput}
                                        placeholder='Last Name'
                                        id="lastName"
                                        name="lastName"
                                        value={formik.values.lastName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                                        helperText={formik.touched.lastName && formik.errors.lastName}
                                    />
                                    <Autocomplete
                                        className={[classes.textInput, classes.autoCompleteField].join(" ")}
                                        label="Relationship*"
                                        id="relationship"
                                        name="relationship"
                                        fullWidth
                                        value={formik.values.relationship}
                                        onChange={relationshipChangeHandler}
                                        onBlur={formik.handleBlur}
                                        options={relationshipOptions}
                                        testId="relationship-input"
                                        error={formik.touched.relationship && Boolean(formik.errors.relationship)}
                                    />
                                    {formik.touched.relationship && Boolean(formik.errors.relationship) && (
                                        <FormHelperText className={classes.dangerTxt}>
                                            {formik.touched.relationship && formik.errors.relationship}
                                        </FormHelperText>
                                    )}
                                    <Autocomplete
                                        className={[classes.textInput, classes.autoCompleteField].join(" ")}
                                        label="Preferred Language*"
                                        id="language"
                                        name="language"
                                        fullWidth
                                        value={formik.values.language}
                                        onChange={languageChangeHandler}
                                        onBlur={formik.handleBlur}
                                        options={languages}
                                        testId="language-input"
                                        error={formik.touched.language && Boolean(formik.errors.language)}
                                    />
                                    {formik.touched.language && Boolean(formik.errors.language) && (
                                        <FormHelperText className={classes.dangerTxt}>
                                            {formik.touched.language && formik.errors.language}
                                        </FormHelperText>
                                    )}
                                    <TextField
                                        icon={(<img src="/us_icon.png" alt="US flag" />)}
                                        className={[classes.textInput, classes.phoneNumber].join(" ")}
                                        label='Phone Number'
                                        placeholder="+1 (###) ###-####"
                                        id="phoneNumber"
                                        name="phoneNumber"
                                        value={formik.values.phoneNumber}
                                        onChange={handlePhoneChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                                        helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                                    />
                                    <TextField
                                        label="Email Address*"
                                        className={classes.textInput}
                                        placeholder="Email Address"
                                        id="email"
                                        name="email"
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.email && Boolean(formik.errors.email)}
                                        helperText={formik.touched.email && formik.errors.email}
                                        disabled={isEdit}
                                    />
                                </>
                            )}

                        </Box>
                    </Box>
                    <ManageContactProvider hookInstance={{
                        selectedFile,
                        previewUrl,
                        isDragging,
                        error,
                        onDrop,
                        onDragEnter,
                        onDragLeave,
                        onFileInputChange,
                        clearSelectedFile,
                        formik
                    }}>
                        {isLoading && (
                            <Skeleton
                                sx={{
                                    width: 517,
                                    height: 300,
                                    borderRadius: '25px !important',
                                    marginTop: '-30px',
                                    marginBottom: '-30px'
                                }}
                                animation="wave" />
                        )}
                        {!isLoading && (
                            <ImageDropArea classes={classes} />
                        )}
                    </ManageContactProvider>
                </form>
            </Modal>
        </Box>
    );
};

export default withStyles(styles)(AddEditContactModal);
